/**
 * Data Objects Service
 * 
 * Smart service loader that selects between real API and mock implementation
 * based on the USE_BACKEND environment variable.
 */

import type { DataSource, FileAnalysis, Folder, FileColumn } from '../types';
import { USE_BACKEND, DEV_MODE } from '../config/env';

// Extended update request type that supports backend API requirements
type DataSourceUpdateRequest = Partial<DataSource> & {
    folderUuid?: string;
    dataObjectUuid?: string;
    description?: string;
    columns?: FileColumn[];
    targetLanguage?: string;
};

export interface DataObjectService {
    getDataSources(folderId?: number): Promise<DataSource[]>;
    getDataSource(id: number | string, includeMetadata?: boolean, targetLanguage?: string): Promise<DataSource>;
    uploadDataSource(file: File, folder?: Folder | number, columns?: any[], description?: string, targetLanguage?: string): Promise<DataSource>;
    replaceDataSourceFile(id: number, file: File, options?: { uuid?: string }): Promise<DataSource>;
    analyzeReplacedFile(uuid: string, file: File, opts?: { description?: string; datasetName?: string; targetLanguage?: string }): Promise<boolean>;
    createDataSourceWithAnalysis(file: File, columns: any[], folder?: Folder | number, description?: string, targetLanguage?: string): Promise<{ dataSource: DataSource; analysisReady: boolean; jobId?: string; }>;
    updateDataSource(id: number, updates: DataSourceUpdateRequest): Promise<DataSource>;
    deleteDataSource(id: number | string): Promise<void>;
    analyzeFile(file: File): Promise<FileAnalysis>;
    analyzeDataSource(id: number, description?: string): Promise<boolean>;
    analyzeDataSourceWithUuid(uuid: string, description?: string): Promise<boolean>;
    createDeltaTableDataSource(fullName: string, columns: any[], description?: string, datasetNameOverride?: string, folder?: Folder): Promise<DataSource>;
    createDeferredRules(dataObjectId: string, columns: FileColumn[], tableName?: string): Promise<void>;
}

// Dynamic service selection based on feature flag
async function createService(): Promise<DataObjectService> {
    try {
        if (USE_BACKEND) {
            const { apiDataObjectService } = await import('./dataObjectService.api');
            // Create a wrapper that adds analyzeFile method for backward compatibility
            const wrapper = {
                async getDataSources(folderId?: number): Promise<DataSource[]> {
                    return apiDataObjectService.getDataSources(folderId);
                },
                async getDataSource(id: number | string, includeMetadata?: boolean, targetLanguage?: string): Promise<DataSource> {
                    return apiDataObjectService.getDataSource(id, includeMetadata, targetLanguage);
                },
                async uploadDataSource(file: File, folder?: Folder | number, columns?: any[], description?: string, targetLanguage?: string): Promise<DataSource> {
                    return apiDataObjectService.uploadDataSource(file, folder, columns, description, targetLanguage);
                },
                async replaceDataSourceFile(id: number, file: File, options?: { uuid?: string }): Promise<DataSource> {
                    return apiDataObjectService.replaceDataSourceFile(id, file, options);
                },
                async analyzeReplacedFile(uuid: string, file: File, opts?: { description?: string; datasetName?: string; targetLanguage?: string }): Promise<boolean> {
                    return apiDataObjectService.analyzeReplacedFile(uuid, file, opts);
                },
                async createDataSourceWithAnalysis(file: File, columns: any[], folder?: Folder | number, description?: string, targetLanguage?: string): Promise<{ dataSource: DataSource; analysisReady: boolean }> {
                    return apiDataObjectService.createDataSourceWithAnalysis(file, columns, folder, description, targetLanguage);
                },
                async updateDataSource(id: number, updates: DataSourceUpdateRequest): Promise<DataSource> {
                    return apiDataObjectService.updateDataSource(id, updates);
                },
                async deleteDataSource(id: number | string): Promise<void> {
                    return apiDataObjectService.deleteDataSource(id);
                },
                async analyzeFile(file: File): Promise<FileAnalysis> {
                    // For now, fall back to mock analysis
                    // TODO: Implement real backend file analysis endpoint
                    const { MockDataObjectService } = await import('./dataObjectService.mock');
                    const mockService = new MockDataObjectService();
                    return mockService.analyzeFile(file);
                },
                async analyzeDataSource(id: number, description?: string): Promise<boolean> {
                    return apiDataObjectService.analyzeDataSource(id, description);
                },
                async analyzeDataSourceWithUuid(uuid: string, description?: string): Promise<boolean> {
                    return apiDataObjectService.analyzeDataSourceWithUuid(uuid, description);
                },
                async createDeltaTableDataSource(fullName: string, columns: any[], description?: string, datasetNameOverride?: string, folder?: Folder): Promise<DataSource> {
                    // Check if method exists on API service
                    if ('createDeltaTableDataSource' in apiDataObjectService && typeof (apiDataObjectService as any).createDeltaTableDataSource === 'function') {
                        return (apiDataObjectService as any).createDeltaTableDataSource(fullName, columns, description, datasetNameOverride, folder);
                    }
                    throw new Error('Databricks delta table creation not available on backend service');
                },
                async createDeferredRules(dataObjectId: string, columns: FileColumn[], tableName?: string): Promise<void> {
                    return apiDataObjectService.createDeferredRules(dataObjectId, columns, tableName);
                }
            };
            return wrapper;
        } else {
            const { MockDataObjectService } = await import('./dataObjectService.mock');
            return new MockDataObjectService();
        }
    } catch (error) {
        console.error('[DataObjectService] Error creating service:', error);
        throw error;
    }
}

// Service instance (lazy-loaded) - clear cache on hot reload
let serviceInstance: Promise<DataObjectService> | null = null;

// Force cache clear for development
if (DEV_MODE) {
    serviceInstance = null;
}

function getService(): Promise<DataObjectService> {
    if (!serviceInstance) {
        serviceInstance = createService();
        // Add error handling for service creation
        serviceInstance.catch(error => {
            console.error('[DataObjectService] Service creation failed:', error);
            serviceInstance = null; // Reset so it can be retried
            throw error;
        });
    }
    return serviceInstance;
}

// Export unified service interface
export const dataObjectService: DataObjectService = {
    async getDataSources(folderId?: number): Promise<DataSource[]> {
        const service = await getService();
        return service.getDataSources(folderId);
    },

    async getDataSource(id: number | string, includeMetadata?: boolean, targetLanguage?: string): Promise<DataSource> {
        const service = await getService();
        return service.getDataSource(id, includeMetadata, targetLanguage);
    },

    async uploadDataSource(file: File, folder?: Folder | number, columns?: any[], description?: string, targetLanguage?: string): Promise<DataSource> {
        const service = await getService();
        return service.uploadDataSource(file, folder, columns, description, targetLanguage);
    },

    async replaceDataSourceFile(id: number, file: File, options?: { uuid?: string }): Promise<DataSource> {
        const service = await getService();
        return service.replaceDataSourceFile(id, file, options);
    },

    async createDataSourceWithAnalysis(file: File, columns: any[], folder?: Folder | number, description?: string, targetLanguage?: string): Promise<{ dataSource: DataSource; analysisReady: boolean; jobId?: string; }> {
        const service = await getService();
        return service.createDataSourceWithAnalysis(file, columns, folder, description, targetLanguage);
    },

    async updateDataSource(id: number, updates: DataSourceUpdateRequest): Promise<DataSource> {
        const service = await getService();
        return service.updateDataSource(id, updates);
    },

    async deleteDataSource(id: number): Promise<void> {
        const service = await getService();
        return service.deleteDataSource(id);
    },

    async analyzeFile(file: File): Promise<FileAnalysis> {
        const service = await getService();
        return service.analyzeFile(file);
    },

    async analyzeDataSource(id: number, description?: string): Promise<boolean> {
        const service = await getService();
        return service.analyzeDataSource(id, description);
    },

    async analyzeDataSourceWithUuid(uuid: string, description?: string): Promise<boolean> {
        const service = await getService();
        return service.analyzeDataSourceWithUuid(uuid, description);
    },

    async analyzeReplacedFile(uuid: string, file: File, opts?: { description?: string; datasetName?: string; targetLanguage?: string }): Promise<boolean> {
        const service = await getService();
        return service.analyzeReplacedFile(uuid, file, opts);
    },

    async createDeltaTableDataSource(fullName: string, columns: any[], description?: string, datasetNameOverride?: string, folder?: Folder): Promise<DataSource> {
        const service = await getService();
        return service.createDeltaTableDataSource(fullName, columns, description, datasetNameOverride, folder);
    },

    async createDeferredRules(dataObjectId: string, columns: FileColumn[], tableName?: string): Promise<void> {
        const service = await getService();

        if (!service.createDeferredRules) {
            throw new Error('createDeferredRules method not found on service');
        }

        return service.createDeferredRules(dataObjectId, columns, tableName);
    }
};